/* eslint-disable class-methods-use-this, no-console */
import * as amplitude from '@amplitude/analytics-browser';
import { consentManager } from './ConsentManager';
import { GA4Helper } from './GA4Helper';
import { FacebookPixelHelper } from './FacebookPixelHelper';
import { ClarityHelper } from './ClarityHelper';

/**
 * Unified AnalyticsHelper class to orchestrate all analytics providers
 * Supports: Amplitude, Google Analytics 4, Facebook Pixel, Microsoft Clarity
 * Handles cookie consent and GDPR compliance
 * @class AnalyticsHelper
 */
export class AnalyticsHelper {
  constructor() {
    this.providers = {
      amplitude: null,
      ga4: null,
      facebook: null,
      clarity: null,
    };
    this.user = {};
    this.initialized = false;
    this.consentManager = consentManager;
  }

  /**
   * Initialize all configured analytics providers based on consent
   * @function
   * @async
   */
  async init() {
    if (this.initialized) return;

    // Initialize consent manager first
    const consent = this.consentManager.init();

    // Initialize providers based on consent
    await this.initializeProviders(consent);

    // Subscribe to consent changes
    this.consentManager.onConsentChange(newConsent => {
      this.handleConsentChange(newConsent);
    });

    // Apply PII masking for Clarity
    this.applyPIIMasking();

    // Re-apply masking on DOM mutations (for SPA navigation)
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => {
        this.applyPIIMasking();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    this.initialized = true;
  }

  /**
   * Initialize analytics providers based on consent
   * @private
   */
  async initializeProviders(consent) {
    // Amplitude (legacy support) - requires analytics consent
    const amplitudeToken = window.analyticsConfig?.token;
    if (amplitudeToken && consent.analytics) {
      try {
        amplitude.init(amplitudeToken, { defaultTracking: false });
        this.providers.amplitude = amplitude;
      } catch (e) {
        console.warn('[Analytics] Failed to initialize Amplitude:', e);
      }
    }

    // GA4 - requires analytics consent
    if (window.gaMeasurementId && consent.analytics) {
      try {
        this.providers.ga4 = new GA4Helper({
          measurementId: window.gaMeasurementId,
        });
        this.providers.ga4.init();
      } catch (e) {
        console.warn('[Analytics] Failed to initialize GA4:', e);
      }
    }

    // Facebook Pixel - requires marketing consent
    if (window.fbPixelId && consent.marketing) {
      try {
        this.providers.facebook = new FacebookPixelHelper({
          pixelId: window.fbPixelId,
        });
        this.providers.facebook.init();
      } catch (e) {
        console.warn('[Analytics] Failed to initialize Facebook Pixel:', e);
      }
    }

    // Microsoft Clarity - requires analytics consent
    if (window.clarityProjectId && consent.analytics) {
      try {
        this.providers.clarity = new ClarityHelper({
          projectId: window.clarityProjectId,
        });
        this.providers.clarity.init();
      } catch (e) {
        console.warn('[Analytics] Failed to initialize Clarity:', e);
      }
    }
  }

  /**
   * Handle consent changes - enable/disable providers dynamically
   * @private
   */
  handleConsentChange(consent) {
    // Re-initialize providers based on new consent
    // Note: Some providers can't be unloaded, but we can stop sending data

    // Amplitude - can't be disabled once loaded, but we can skip tracking
    if (!consent.analytics && this.providers.amplitude) {
      // Will be skipped in track() calls
    } else if (consent.analytics && !this.providers.amplitude) {
      const amplitudeToken = window.analyticsConfig?.token;
      if (amplitudeToken) {
        amplitude.init(amplitudeToken, { defaultTracking: false });
        this.providers.amplitude = amplitude;
      }
    }

    // GA4 - update consent settings
    if (this.providers.ga4) {
      this.providers.ga4.updateConsent(consent.analytics);
    } else if (consent.analytics && window.gaMeasurementId) {
      this.providers.ga4 = new GA4Helper({
        measurementId: window.gaMeasurementId,
      });
      this.providers.ga4.init();
    }

    // Facebook Pixel - update consent
    if (this.providers.facebook) {
      this.providers.facebook.updateConsent(consent.marketing);
    } else if (consent.marketing && window.fbPixelId) {
      this.providers.facebook = new FacebookPixelHelper({
        pixelId: window.fbPixelId,
      });
      this.providers.facebook.init();
    }

    // Clarity - can't disable once loaded
    if (!consent.analytics && this.providers.clarity) {
      // Will be skipped in track() calls
    } else if (
      consent.analytics &&
      !this.providers.clarity &&
      window.clarityProjectId
    ) {
      this.providers.clarity = new ClarityHelper({
        projectId: window.clarityProjectId,
      });
      this.providers.clarity.init();
    }
  }

  /**
   * Identify the user across all providers (only if consent allows)
   * @function
   * @param {Object} user - User object
   */
  identify(user) {
    if (!user) return;
    this.user = user;

    const consent = this.consentManager.getConsent();

    // Hash sensitive data for privacy
    const hashedUserId = this.hashUserId(user.id);

    // Amplitude identify (requires analytics consent)
    if (this.providers.amplitude && consent.analytics) {
      try {
        this.providers.amplitude.setUserId(`user-${user.id.toString()}`);
        const identifyEvent = new this.providers.amplitude.Identify();
        identifyEvent.set('email', user.email);
        identifyEvent.set('name', user.name);
        identifyEvent.set('avatar', user.avatar_url);
        this.providers.amplitude.identify(identifyEvent);

        const { accounts, account_id: accountId } = user;
        const [currentAccount] = accounts.filter(acc => acc.id === accountId);
        if (currentAccount) {
          const groupId = `account-${currentAccount.id.toString()}`;
          this.providers.amplitude.setGroup('company', groupId);
          const groupIdentify = new this.providers.amplitude.Identify();
          groupIdentify.set('name', currentAccount.name);
          this.providers.amplitude.groupIdentify(
            'company',
            groupId,
            groupIdentify
          );
        }
      } catch (e) {
        console.warn('[Analytics] Amplitude identify error:', e);
      }
    }

    // GA4 identify (requires analytics consent)
    if (this.providers.ga4 && consent.analytics) {
      try {
        this.providers.ga4.identify(user, hashedUserId);
      } catch (e) {
        console.warn('[Analytics] GA4 identify error:', e);
      }
    }

    // Facebook Pixel identify (requires marketing consent)
    if (this.providers.facebook && consent.marketing) {
      try {
        this.providers.facebook.identify(user);
      } catch (e) {
        console.warn('[Analytics] Facebook identify error:', e);
      }
    }

    // Clarity identify (requires analytics consent)
    if (this.providers.clarity && consent.analytics) {
      try {
        this.providers.clarity.identify(user, hashedUserId);
      } catch (e) {
        console.warn('[Analytics] Clarity identify error:', e);
      }
    }
  }

  /**
   * Hash user ID for privacy compliance
   * @private
   */
  hashUserId(userId) {
    // Simple hash for demo - use SHA-256 in production
    return `user_${userId}`;
  }

  /**
   * Apply PII masking to sensitive elements for Clarity
   * Adds data-clarity-mask="true" to elements containing sensitive data
   * @private
   */
  applyPIIMasking() {
    // Mask email input fields
    const emailInputs = document.querySelectorAll(
      'input[type="email"], input[name*="email" i]'
    );
    emailInputs.forEach(el => el.setAttribute('data-clarity-mask', 'true'));

    // Mask password fields
    const passwordInputs = document.querySelectorAll('input[type="password"]');
    passwordInputs.forEach(el => el.setAttribute('data-clarity-mask', 'true'));

    // Mask phone inputs
    const phoneInputs = document.querySelectorAll(
      'input[type="tel"], input[name*="phone" i], input[name*="mobile" i]'
    );
    phoneInputs.forEach(el => el.setAttribute('data-clarity-mask', 'true'));

    // Mask credit card fields
    const cardInputs = document.querySelectorAll(
      'input[name*="card" i], input[name*="cc-" i], input[name*="credit" i]'
    );
    cardInputs.forEach(el => el.setAttribute('data-clarity-mask', 'true'));

    // Mask elements with sensitive classes
    const sensitiveElements = document.querySelectorAll(
      '.email, .phone, .credit-card, .ssn, .password, [data-sensitive]'
    );
    sensitiveElements.forEach(el =>
      el.setAttribute('data-clarity-mask', 'true')
    );

    // Mask conversation content in chatwoot
    const chatMessages = document.querySelectorAll(
      '.conversation-panel, .message-content, .bubble, .messages-list'
    );
    chatMessages.forEach(el => el.setAttribute('data-clarity-mask', 'true'));
  }

  /**
   * Track an event across all allowed providers
   * @function
   * @param {string} eventName - Event name
   * @param {Object} [properties={}] - Event properties
   * @param {Object} [options={}] - Provider-specific options
   */
  track(eventName, properties = {}, options = {}) {
    const consent = this.consentManager.getConsent();

    // Standardize event names for different providers
    const amplitudeEvent = eventName;
    const ga4Event = this.standardizeGA4EventName(eventName);
    const fbEvent = this.standardizeFacebookEventName(eventName);

    // Add common properties
    const enrichedProperties = {
      ...properties,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      path: window.location.pathname,
    };

    // Amplitude (requires analytics consent)
    if (
      this.providers.amplitude &&
      consent.analytics &&
      options.amplitude !== false
    ) {
      try {
        this.providers.amplitude.track(amplitudeEvent, enrichedProperties);
      } catch (e) {
        console.warn('[Analytics] Amplitude track error:', e);
      }
    }

    // GA4 (requires analytics consent)
    if (this.providers.ga4 && consent.analytics && options.ga4 !== false) {
      try {
        this.providers.ga4.track(ga4Event, enrichedProperties);
      } catch (e) {
        console.warn('[Analytics] GA4 track error:', e);
      }
    }

    // Facebook Pixel (requires marketing consent)
    if (
      this.providers.facebook &&
      consent.marketing &&
      options.facebook !== false
    ) {
      try {
        this.providers.facebook.track(fbEvent, enrichedProperties);
      } catch (e) {
        console.warn('[Analytics] Facebook track error:', e);
      }
    }

    // Clarity (requires analytics consent)
    if (
      this.providers.clarity &&
      consent.analytics &&
      options.clarity !== false
    ) {
      try {
        this.providers.clarity.track(eventName, enrichedProperties);
      } catch (e) {
        console.warn('[Analytics] Clarity track error:', e);
      }
    }
  }

  /**
   * Track page views across all providers
   * @function
   * @param {string} pageName - Page name
   * @param {Object} [properties={}] - Page view properties
   */
  page(pageName, properties = {}) {
    const consent = this.consentManager.getConsent();

    const pageData = {
      page_name: pageName,
      page_path: window.location.pathname,
      page_url: window.location.href,
      page_title: document.title,
      ...properties,
    };

    // Amplitude
    if (this.providers.amplitude && consent.analytics) {
      try {
        this.providers.amplitude.track('$pageview', pageData);
      } catch (e) {
        console.warn('[Analytics] Amplitude page error:', e);
      }
    }

    // GA4 - Send SPA page view
    if (consent.analytics) {
      try {
        if (window.gtag) {
          window.gtag('event', 'page_view', {
            page_name: pageName,
            page_title: pageData.page_title,
            page_path: pageData.page_path,
            page_location: pageData.page_url,
            ...properties,
          });
        }
      } catch (e) {
        console.warn('[Analytics] GA4 page error:', e);
      }
    }

    // Facebook Pixel
    if (consent.marketing) {
      try {
        if (window.fbq) {
          window.fbq('track', 'PageView');
        }
      } catch (e) {
        console.warn('[Analytics] Facebook page error:', e);
      }
    }

    // Clarity
    if (this.providers.clarity && consent.analytics) {
      try {
        this.providers.clarity.page(pageName, pageData);
      } catch (e) {
        console.warn('[Analytics] Clarity page error:', e);
      }
    }
  }

  /**
   * Setup Vue Router tracking for SPA navigation
   * Call this after router is initialized
   * @param {Object} router - Vue Router instance
   */
  setupRouterTracking(router) {
    if (!router) return;

    // Track initial page view
    this.page(router.currentRoute.value?.name || 'initial');

    // Track route changes
    router.afterEach((to, from) => {
      // Small delay to ensure page title is updated
      setTimeout(() => {
        this.page(to.name || to.path, {
          from_path: from?.path,
          to_path: to.path,
          route_name: to.name,
        });
      }, 50);
    });

    console.log('[Analytics] Router tracking enabled');
  }

  /**
   * Track ecommerce/conversion events
   * @param {string} eventName - Ecommerce event name
   * @param {Object} data - Transaction data
   */
  trackEcommerce(eventName, data = {}) {
    const consent = this.consentManager.getConsent();

    // Only track if marketing consent given (purchases are marketing-related)
    if (!consent.marketing) return;

    // GA4 ecommerce
    if (this.providers.ga4) {
      try {
        this.providers.ga4.trackEcommerce(eventName, data);
      } catch (e) {
        console.warn('[Analytics] GA4 ecommerce error:', e);
      }
    }

    // Facebook Pixel purchase
    if (this.providers.facebook && eventName === 'purchase') {
      try {
        this.providers.facebook.trackPurchase(data);
      } catch (e) {
        console.warn('[Analytics] Facebook purchase error:', e);
      }
    }
  }

  /**
   * Standardize event names for GA4 (snake_case)
   * @private
   */
  standardizeGA4EventName(eventName) {
    return eventName
      .replace(/\s+/g, '_')
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase();
  }

  /**
   * Standardize event names for Facebook Pixel
   * @private
   */
  standardizeFacebookEventName(eventName) {
    const standardEvents = {
      // Registration & Subscription
      'Signup Completed': 'CompleteRegistration',
      'Account Created': 'CompleteRegistration',
      'Trial Started': 'StartTrial',
      'Subscription Started': 'Subscribe',
      'Purchase Completed': 'Purchase',
      'Payment Successful': 'Purchase',
      'Plan Upgraded': 'Purchase',

      // Product Engagement
      'Inbox Created': 'CustomizeProduct',
      'Agent Added': 'CustomizeProduct',
      'Added an inbox': 'CustomizeProduct',
      'Created a label': 'CustomizeProduct',
      'Updated a label': 'CustomizeProduct',
      'Created a portal': 'CustomizeProduct',
      'Created a portal locale': 'CustomizeProduct',
      'Created a portal category': 'CustomizeProduct',
      'Edited a portal category': 'CustomizeProduct',

      // Search & Discovery
      'Searched conversations': 'Search',
      'Applied filters in the conversation list': 'Search',
      'Searched contacts list': 'Search',
      'Applied filters in the contacts list': 'Search',
      'Sorted contacts list': 'Search',

      // Content Engagement
      'Downloaded a report': 'ViewContent',
      'Filtered report': 'ViewContent',
      'Created an article': 'ViewContent',
      'Published an article': 'ViewContent',
      'Previewed article': 'ViewContent',

      // Lead Generation (High Intent)
      'Created a campaign': 'Lead',
      'Created an SLA': 'Lead',
      'Updated an SLA': 'Lead',
      'Used OpenAI: Used summarize': 'Lead',
      'Used OpenAI: Used reply suggestion': 'Lead',
      'Copilot: Sent a message': 'Lead',
      'Used commandbar': 'Lead',
    };
    return standardEvents[eventName] || 'CustomEvent';
  }

  /**
   * Reset analytics (for logout)
   */
  reset() {
    // Reset user identification
    this.user = {};

    // Reset Amplitude
    if (this.providers.amplitude) {
      try {
        this.providers.amplitude.reset();
      } catch (e) {
        console.warn('[Analytics] Amplitude reset error:', e);
      }
    }

    // Reset GA4
    if (this.providers.ga4) {
      try {
        this.providers.ga4.reset();
      } catch (e) {
        console.warn('[Analytics] GA4 reset error:', e);
      }
    }

    // Note: Facebook and Clarity don't have reset methods
  }
}

// Export individual helpers
export { GA4Helper, FacebookPixelHelper, ClarityHelper, consentManager };

// Default instance
export default new AnalyticsHelper();
